import { useState, useEffect } from 'react';
import { CreditCard, Coins, Loader2, AlertTriangle } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { useProjectStore } from '@/stores/useProject.store';

// This step handles its own navigation via window.location.href on success (same as before).
// onSubmit is NOT called here — payment redirects away from the app.
const BaseFeesPaymentStep = ({ onBack, context, permitChatId }) => {
  const { permitId, userId } = context || {};
  const markPermitChatBaseFeesPaid = useProjectStore(state => state.markPermitChatBaseFeesPaid);
  const getPermitChatGroups = useProjectStore(state => state.getPermitChatGroups);

  const [permitData, setPermitData] = useState(null);
  const [permitDataLoading, setPermitDataLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);
  const [baseFeeAmount, setBaseFeeAmount] = useState(null);
  const [baseFeeCurrency, setBaseFeeCurrency] = useState('usd');
  const [creditsLeft, setCreditsLeft] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [creditUseLoading, setCreditUseLoading] = useState(false);


  const getPaymentDetails = async () => {
    setPaymentDetailsLoading(true);
    try {
      const res = await appAxios.get('/Permit/v1.0/base-fees-price-details');
      setBaseFeeAmount(res.data.data.stripe.display_price);
      setBaseFeeCurrency(res.data.data.stripe.currency);
    } catch (err) {
      console.error('Failed to fetch payment details:', err);
    } finally {
      setPaymentDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (!permitId) return;
    setPermitDataLoading(true);
    getPaymentDetails();
    appAxios
      .get('/Permit/v1.0/permit-details', { params: { permitId } })
      .then(res => setPermitData(res.data.data))
      .catch(err => console.error('Failed to fetch permit data:', err))
      .finally(() => setPermitDataLoading(false));
  }, [permitId]);

  useEffect(() => {
    setCreditsLoading(true);
    appAxios
      .get('/Permit/v1.0/check-credits')
      .then(res => { if (res.data?.status === 'Success') setCreditsLeft(res.data.data); })
      .catch(() => { })
      .finally(() => setCreditsLoading(false));
  }, []);

  const handlePay = async () => {
    setPayLoading(true);
    try {
      const currentUrl = window.location.href;
      const res = await appAxios.post('/Payment/v1.0/create-base-fees-payment-link', {
        permitId,
        userId,
        permitChatId,
        successUrl: currentUrl,
        cancelUrl: currentUrl,
      });
      if (res.data?.status === 'Success') {
        window.location.href = res.data.data.url;
      } else {
        toast.error('Error creating payment link. Please try again.');
      }
    } catch {
      toast.error('Error creating payment link. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleUseCredit = async () => {
    setCreditUseLoading(true);
    try {
      const res = await appAxios.post('/Permit/v1.0/update-credits', { permitId });
      if (res.data?.status === 'Success' || res.data?.success) {
        toast.success('Credit applied! Your permit application is being processed.');
        const confirmedPermitId = res.data?.data?._id || permitId;
        localStorage.setItem(`permitChat_${permitChatId}_permitId`, confirmedPermitId);
        markPermitChatBaseFeesPaid(permitChatId);
        if (res.data?.data?._id) {
          getPermitChatGroups(null, true);
          window.location.href = `/citypermit/project/chat/${permitChatId}/permit/${res.data.data._id}`;
        } else {
          window.location.href = '/citypermit/project';
        }
      } else {
        toast.error('Failed to apply credit. Please try again.');
      }
    } catch {
      toast.error('Failed to apply credit. Please try again.');
    } finally {
      setCreditUseLoading(false);
    }
  };

  const baseFee = baseFeeAmount ?? permitData?.base_fees_amount ?? 49.99;
  const feeFormatted = Intl.NumberFormat('en-US', { style: 'currency', currency: baseFeeCurrency.toUpperCase() }).format(baseFee);
  const isProcessing = payLoading || creditUseLoading || permitDataLoading;

  return (
    <div className="space-y-3 w-full">
      {/* Pay with Card */}
      <button
        onClick={handlePay}
        disabled={isProcessing || paymentDetailsLoading}
        className="w-full rounded-lg border border-gray-700 bg-[#1e1e1e] hover:bg-[#282828] p-4 text-left transition-colors disabled:opacity-50"
      >
        {paymentDetailsLoading ? (
          <div className="flex items-center justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex items-center justify-center" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-blue-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Pay with Card</p>
                <p className="text-xs text-gray-400 mt-0.5">One-time payment to process your application</p>
              </div>
              {payLoading || permitDataLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <span className="text-base font-semibold text-blue-400">{feeFormatted}</span>
              )}
            </div>
          </>
        )}
      </button>

      {/* Fee disclaimer */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-700/40 bg-amber-900/20 p-3 text-xs text-amber-300">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          This {feeFormatted} is the Zermit service fee only. City permit fees are not included, and those will be
          determined by the city after your application is reviewed and will be charged to you directly and separately.
        </span>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <div className="flex-1 h-px bg-gray-700" />
        <span>or</span>
        <div className="flex-1 h-px bg-gray-700" />
      </div>

      {/* Use Credit */}
      <div className="rounded-lg border border-gray-700 bg-[#1e1e1e] p-4">
        <div className="flex items-center gap-3">
          <Coins className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-white">Use a Credit</p>
            <p className="text-xs text-gray-400 mt-0.5">For subscribed users</p>
          </div>
          {creditsLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {!creditsLoading && creditsLeft !== null && (
            <span className="text-xs text-gray-400">{creditsLeft} left</span>
          )}
        </div>
        {!creditsLoading && creditsLeft > 0 && (
          <button
            onClick={handleUseCredit}
            disabled={isProcessing}
            className="mt-3 w-full bg-blue-600 hover:bg-blue-500 text-white py-2 rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition"
          >
            {creditUseLoading && <Loader2 className="w-3 h-3 animate-spin" />}
            Use 1 Credit to Process
          </button>
        )}
        {!creditsLoading && (creditsLeft === null || creditsLeft === 0) && (
          <p className="mt-3 text-xs text-gray-500 text-center">
            {creditsLeft === 0 ? 'No credits remaining.' : 'No Active Subscription Found'}
          </p>
        )}
      </div>

      {/* <button onClick={onBack} disabled={isProcessing} className="w-full border border-gray-700 hover:border-gray-500 text-white py-3 rounded-lg transition text-sm disabled:opacity-50">Back</button> */}
    </div>
  );
};

export default BaseFeesPaymentStep;
